import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Typography } from "@mui/material";
import html2pdf from "html2pdf.js";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomSelect from "../../../../components/Select/Select";
import {
  updateAppointmentStatusById,
  updateCaseSummaryUrlByApntId,
  updateFinalize,
} from "../../../../redux/reducers/clinicSlice";
import { getEmrSettings } from "../../../../redux/reducers/globalSettingsSlice";
import {
  getPetsCompliantSummaryWoLoader,
  petAppointment,
} from "../../../../redux/reducers/petSlice";
import { getClinicVets } from "../../../../redux/reducers/vetSlice";
import { AppColors } from "../../../../util/AppColors";
import { a4PrintConfig } from "../../../../util/commonConfig";
import { workbookStatusList } from "../../../../util/dropList";
import { genderEnum } from "../../../../util/enum";
import { printFromNode, warmPrintFrame } from "../../../../util/fastPrint";
import { adjustTime, getRightSaluation } from "../../../../util/function";
import ContentToPrint from "../../../CommonScreens/ContentToPrint/ContentToPrint";
import BodyMapView from "../../../CommonScreens/WorkBook/BodyMapView";
import DiagnosticsView from "../../../CommonScreens/WorkBook/DiagnosticsView";
import FileView from "../../../CommonScreens/WorkBook/FileView";
import FormsView from "../../../CommonScreens/WorkBook/FormsView";
import PrescriptionView from "../../../CommonScreens/WorkBook/PrescriptionView";
import PreventiveView from "../../../CommonScreens/WorkBook/PreventiveView";
import ProceduresView from "../../../CommonScreens/WorkBook/ProceduresView";
import SoapNoteView from "../../../CommonScreens/WorkBook/SoapNoteView";
import TreatmentPlanView from "../../../CommonScreens/WorkBook/TreatmentPlanView";
import VitalsView from "../../../CommonScreens/WorkBook/VitalsView";

const initOpen = {
  overview: true,
  procedures: true,
  administered: true,
  preventive: true,
  prescription: true,
  diagnostics: true,
  soapNotes: true,
  surgeryDoc: true,
  treatmentPlan: true,
  forms: true,
  vitals: true,
  bodyMap: true,
  files: true,
  ioMonitoring: true,
};

const iconSty = { width: 25, height: 25, color: AppColors.appPrimary };

const defObj = { locations: [], facilities: [] };

const getInpatientAlertList = (raw) =>
  Array.isArray(raw) ? raw.filter((al) => String(al?.text ?? "").trim()) : [];

const getSortedInpatientAlerts = (raw) =>
  [...getInpatientAlertList(raw)].sort((a, b) => {
    const aTime = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
    const bTime = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return Number(b?.alertId ?? 0) - Number(a?.alertId ?? 0);
  });

const getInpatientSummaryList = (raw) =>
  Array.isArray(raw)
    ? raw.filter((sm) => String(sm?.instructions ?? "").trim())
    : [];

const getSortedInpatientSummaries = (raw) =>
  [...getInpatientSummaryList(raw)].sort((a, b) => {
    const aTime = new Date(a?.createdDate || 0).getTime();
    const bTime = new Date(b?.createdDate || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return Number(b?.summaryId ?? 0) - Number(a?.summaryId ?? 0);
  });

const AdmissionRemarkView = ({ remarks }) => {
  const text = String(remarks ?? "").trim();
  if (!text) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Reason for Admission / Provisional Diagnosis
        </Typography>
      </div>
      <Typography
        className="fs12 txt-regular black mt5"
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
        component="div"
      >
        {text.split("\n").map((line, index) => (
          <div key={`admission-remark-${index}`}>{line || "\u00A0"}</div>
        ))}
      </Typography>
    </>
  );
};

const InpatientRedFlagsView = ({ inpatientAlerts }) => {
  const alerts = getSortedInpatientAlerts(inpatientAlerts);
  if (!alerts.length) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">Red Flags</Typography>
      </div>
      <div
        className="mt5"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fafafa",
          padding: "10px 12px",
        }}
      >
        {alerts.map((alrt) => {
          const created = moment(alrt?.updatedDate || alrt?.createdDate);
          const time = created.isValid()
            ? created.format("DD MMM YYYY, hh:mm a")
            : "";
          return (
            <div key={alrt?.alertId} style={{ marginBottom: 8 }}>
              <Typography
                className="fs12 txt-regular black"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                component="div"
              >
                {(alrt?.text ?? "")?.split("\n")?.map((line, index) => (
                  <div key={`${alrt?.alertId}-${index}`}>
                    {line || "\u00A0"}
                  </div>
                ))}
              </Typography>
              {time || alrt?.doctorName ? (
                <Typography className="fs10 font-medium gray4">
                  {[alrt?.doctorName, time].filter(Boolean).join(" · ")}
                </Typography>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

const InpatientInstructionsView = ({ inpatientSummary }) => {
  const summaries = getSortedInpatientSummaries(inpatientSummary);
  if (!summaries.length) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Instructions
        </Typography>
      </div>
      <div
        className="mt5"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fafafa",
          padding: "10px 12px",
        }}
      >
        {summaries.map((inp) => {
          const created = moment(inp?.createdDate);
          const time = created.isValid()
            ? created.format("DD MMM YYYY, hh:mm a")
            : "";
          return (
            <div key={inp?.summaryId} style={{ marginBottom: 8 }}>
              <Typography
                className="fs12 txt-regular black"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                component="div"
              >
                {(inp?.instructions ?? "")?.split("\n")?.map((line, index) => (
                  <div key={`${inp?.summaryId}-${index}`}>
                    {line || "\u00A0"}
                  </div>
                ))}
              </Typography>
              {time || inp?.doctorName ? (
                <Typography className="fs10 font-medium gray4">
                  {[inp?.doctorName, time].filter(Boolean).join(" · ")}
                </Typography>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

const getIoMonitoringList = (raw) => {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.content)
      ? raw.content
      : Array.isArray(raw?.ioMonitoring)
        ? raw.ioMonitoring
        : [];
  return list.filter(
    (item) => item != null && typeof item === "object" && !Array.isArray(item),
  );
};

const safeIoText = (value, fallback = "—") => {
  if (value == null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim();
    return text || fallback;
  }
  return fallback;
};

const buildIoLogDescription = (form) => {
  try {
    if (!form || typeof form !== "object") return "—";

    const category = String(form?.category || "")
      .toLowerCase()
      .trim();

    if (category === "iv fluid") {
      if (!form?.fluidType && !form?.rate && !form?.volumeInfused) {
        return "— running at ml/hr, ml infused so far";
      }
      return `${safeIoText(form.fluidType)} — running at ${safeIoText(form.rate, "")} ml/hr, ${safeIoText(form.volumeInfused, "")} ml infused since start`;
    }
    if (category === "oral water") {
      return `Offered ${safeIoText(form.offeredMl)} ml, consumed ${safeIoText(form.consumedMl)} ml`;
    }
    if (category === "food") {
      return `${safeIoText(form.dietFoodType, "Food")} — offered ${safeIoText(form.offeredG)} g, consumed ${safeIoText(form.consumedG)}`;
    }
    if (category === "blood / blood products") {
      return `${safeIoText(form.productType, "Blood product")} — ${safeIoText(form.volumeMl)} ml at ${safeIoText(form.rate)} ml/hr`;
    }
    if (category === "urine") {
      return `${form?.volume != null && form?.volume !== "" ? `${form.volume} ml` : "—"}, ${safeIoText(form.colourCharacter)}`;
    }
    if (category === "vomiting") {
      const episodes = form?.episodes || "1";
      const epLabel = String(episodes) === "1" ? "episode" : "episodes";
      return `${episodes} ${epLabel}, ${safeIoText(form.character || form.characterValue)}`;
    }
    if (category === "feces") {
      return `${safeIoText(form.fecalScore)}, ${safeIoText(form.notes, "")}`.trim();
    }
    if (category === "blood loss / hemorrhage") {
      return `${safeIoText(form.estimatedVolume)}, source: ${safeIoText(form.sourceSite)}`;
    }
    if (category.startsWith("other (")) {
      return safeIoText(form.description);
    }
    if (category === "other nursing note") {
      return safeIoText(form.description);
    }
    if (category === "pressure area check") {
      return safeIoText(form.finding);
    }
    if (
      category.includes("catheter") ||
      category.includes("wound") ||
      category.includes("incision")
    ) {
      return `${safeIoText(form.site)} — ${safeIoText(form.finding)}`;
    }
    return safeIoText(form.description);
  } catch {
    return "—";
  }
};

const parseIoMonitoringEntry = (item, index) => {
  try {
    const rawDate =
      item?.createdDate || item?.createdAt || item?.updatedDate || null;
    const created = rawDate ? moment(rawDate) : null;
    const isValidDate = Boolean(created?.isValid?.());
    const descriptionRaw =
      item?.description != null ? String(item.description).trim() : "";

    return {
      id:
        item?.ioMonitoringId ??
        item?.id ??
        `io-monitoring-${index}-${item?.category || item?.type || "entry"}`,
      categoryLabel: safeIoText(item?.category || item?.type, ""),
      description: descriptionRaw || buildIoLogDescription(item),
      time: isValidDate ? created.format("DD MMM YYYY, hh:mm a") : "",
      sortTime: isValidDate ? created.valueOf() : 0,
    };
  } catch {
    return {
      id: `io-monitoring-fallback-${index}`,
      categoryLabel: "",
      description: "—",
      time: "",
      sortTime: 0,
    };
  }
};

const IOMonitoringSummaryView = ({ ioMonitoring, hideTitle = false }) => {
  let entries = [];
  try {
    entries = getIoMonitoringList(ioMonitoring)
      .map((item, index) => parseIoMonitoringEntry(item, index))
      .sort((a, b) => (b?.sortTime || 0) - (a?.sortTime || 0));
  } catch {
    entries = [];
  }

  if (!entries.length) return null;

  return (
    <>
      {!hideTitle ? (
        <div className="mt10 flex-row-ali-cen">
          <Typography className="font-bold fs14 blue-color">
            Monitoring & I/O Summary
          </Typography>
        </div>
      ) : null}
      <div
        className="mt5 mh20"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fafafa",
          padding: "10px 12px",
        }}
      >
        {entries.map((item) => (
          <div key={String(item.id)} style={{ marginBottom: 8 }}>
            <Typography className="fs12 txt-regular black">
              {item.categoryLabel ? (
                <span className="txt-semi-bold">{item.categoryLabel}: </span>
              ) : null}
              {item.description || "—"}
            </Typography>
            {item.time ? (
              <Typography className="fs10 font-medium gray4">
                {item.time}
              </Typography>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
};

/** WorkBookHistory print only — does not change shared a4PrintConfig used elsewhere. */
const workbookHistoryPrintConfig = `${a4PrintConfig}
@media print {
  .workbook-history-print.prescription-print-root .prescription-print-thead-cell {
    padding: 0 0 4px 0;
  }
  .workbook-history-print.prescription-print-root .prescription-print-tbody-cell {
    padding: 2px 0 4px;
  }
  .workbook-history-print.prescription-print-root .prescription-print-doc-header {
    padding: 2px 4px 4px;
  }
  .workbook-history-print.prescription-print-root .prescription-print-body-inner {
    padding: 0 0 2px;
  }
  .workbook-history-print.prescription-print-root .mv20 {
    margin-top: 2px !important;
    margin-bottom: 2px !important;
  }
  .workbook-history-print.prescription-print-root .mv15 {
    margin: 4px 0 !important;
  }
  .workbook-history-print.prescription-print-root .mv10,
  .workbook-history-print.prescription-print-root .mv5 {
    margin-top: 2px !important;
    margin-bottom: 2px !important;
  }
  .workbook-history-print.prescription-print-root .mb20 {
    margin-bottom: 4px !important;
  }
  .workbook-history-print.prescription-print-root .mb10 {
    margin-bottom: 3px !important;
  }
  .workbook-history-print.prescription-print-root .mt20,
  .workbook-history-print.prescription-print-root .mt15 {
    margin-top: 4px !important;
  }
  .workbook-history-print.prescription-print-root .mt10 {
    margin-top: 3px !important;
  }
  .workbook-history-print.prescription-print-root .mt10:empty:not(.blk-hor-split):not(.gry-hor-split):not(.gry-hor-split2):not(.gry-hor-split3):not(.gry-hor-split4),
  .workbook-history-print.prescription-print-root .mt5:empty:not(.blk-hor-split):not(.gry-hor-split),
  .workbook-history-print.prescription-print-root .mb20:empty:not(.blk-hor-split):not(.gry-hor-split) {
    display: none !important;
    margin: 0 !important;
    padding: 0 !important;
    height: 0 !important;
  }
  .workbook-history-print.prescription-print-root .blk-hor-split,
  .workbook-history-print.prescription-print-root .gry-hor-split {
    display: block !important;
    height: 1px !important;
    margin-top: 2px !important;
    margin-bottom: 2px !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .workbook-history-print.prescription-print-root .blk-hor-split {
    background: #000 !important;
  }
  .workbook-history-print.prescription-print-root .gry-hor-split {
    background: #ececec !important;
    height: 2px !important;
  }
  .workbook-history-print.prescription-print-root .mt5 {
    margin-top: 1px !important;
  }
  .workbook-history-print.prescription-print-root .mh20,
  .workbook-history-print.prescription-print-root .mh15 {
    margin-left: 4px !important;
    margin-right: 4px !important;
  }
  .workbook-history-print.prescription-print-root .ph20,
  .workbook-history-print.prescription-print-root .ph15,
  .workbook-history-print.prescription-print-root .ph10 {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
  .workbook-history-print.prescription-print-root .p5 {
    padding: 1px !important;
  }
  .workbook-history-print.prescription-print-root .prescription-print-notes {
    margin-top: 3px !important;
    margin-bottom: 2px !important;
  }
  .workbook-history-print.prescription-print-root .workbook-print-section,
  .workbook-history-print.prescription-print-root
    .MuiTypography-root.workbook-print-section {
    margin-top: 10px !important;
  }
  .workbook-history-print.prescription-print-root
    .prescription-print-notes.workbook-print-section {
    margin-top: 10px !important;
  }
  .workbook-history-print.prescription-print-root .img-h75 {
    max-height: 48px !important;
    height: auto !important;
  }
  .workbook-history-print.prescription-print-root .fs20 {
    font-size: 14px !important;
  }
  .workbook-history-print.prescription-print-root .fs16 {
    font-size: 12px !important;
  }
  .workbook-history-print.prescription-print-root .fs14 {
    font-size: 11px !important;
    line-height: 1.25 !important;
  }
  .workbook-history-print.prescription-print-root .fs12 {
    font-size: 10px !important;
    line-height: 1.25 !important;
  }
  .workbook-history-print.prescription-print-root p,
  .workbook-history-print.prescription-print-root .MuiTypography-root {
    orphans: 2;
    widows: 2;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    line-height: 1.25 !important;
  }
}
`;

const WorkBookHistory = ({
  finModVsble,
  setFinModVsble,
  handleFinalize,
  isSumamryView = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pdfRef = useRef(null);
  const [status, setStatus] = useState("Active");
  const [profileImg, setProfileImg] = useState(null);
  const [openValues, setOpenValues] = useState(initOpen);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const inpatData =
    reqDet?.inpatientResponse ?? petAndApntData?.inpatientResponse ?? {};
  const {
    clinicStocks,
    apiInProgress,
    details: clinicDet,
  } = useSelector((state) => state?.clinic);
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const emrSettings = useSelector(
    (state) => state?.globalSettings?.emrSettings,
  );
  const isEmrEnabled = (key) => {
    if (!emrSettings || typeof emrSettings !== "object") return true;
    if (emrSettings[key] == null) return true;
    return !!emrSettings[key];
  };
  const apntId = reqDet?.appointment?.appointmentId;
  const apntDet = reqDet?.appointment;
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);
  const isAnyApiInProgress =
    apiInProgress?.soapGeneration ||
    apiInProgress?.clinicNotesPost ||
    apiInProgress?.clinicNotesPut ||
    apiInProgress?.consolidateSoap;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { locations, facilities } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
        facilities: Array.isArray(reqData?.facilities)
          ? reqData?.facilities
          : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  // AWS.config.update({
  //   accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  //   secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  //   region: process.env.REACT_APP_AWS_REGION,
  // });

  // const s3 = new AWS.S3();
  // const bucketName = process.env.REACT_APP_AWS_BUGET_NAME;

  useEffect(() => {
    if (finModVsble) {
      dispatch(getPetsCompliantSummaryWoLoader(apntId));
      //for the purpose of storing stock issue list
      dispatch(getClinicVets());
      dispatch(getEmrSettings({ silent: true }));
      // dispatch(getLocations());
      // dispatch(getClinicFacilities());
    }
  }, [finModVsble]);

  useEffect(() => {
    if (clinicDet?.image) {
      fetch(clinicDet.image)
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => setProfileImg(reader.result);
        });
    }
  }, [clinicDet?.image]);

  const onCloseMod = () => {
    setFinModVsble(false);
    setOpenValues(initOpen);
  };

  const handleOpen = (name, value) => {
    setOpenValues({ ...openValues, [name]: value });
  };

  const getcompliantSummary = () => {
    dispatch(getPetsCompliantSummaryWoLoader(apntId));
  };

  // const uploadPdfToS3 = (blob, filename) => {
  //   return new Promise((resolve, reject) => {
  //     const params = {
  //       Bucket: bucketName,
  //       Key: filename,
  //       Body: blob,
  //       ContentType: "application/pdf",
  //     };
  //     s3.upload(params, (err, data) => {
  //       if (err) {
  //         console.error("Error uploading to S3:", err);
  //         reject(err);
  //       } else {
  //         resolve(data?.Location);
  //       }
  //     });
  //   });
  // };

  const getCompletedAdministerList = () => {
    return (
      reqDet?.procedures?.flatMap((li) =>
        li?.procedureDetails?.filter(
          (det) =>
            det?.type === "Administered" &&
            det?.stockId &&
            det?.batchId &&
            det?.status === "true",
        ),
      ) ?? []
    );
  };

  const getVetDepartment = () => {
    const reqDept =
      clinicVets?.find((vl) => vl?.doctorId === apntDet?.doctorId)
        ?.department ?? null;
    return reqDept;
  };

  const getProductNameByStockId = (stckId) => {
    return (
      clinicStocks?.data?.find((stc) => stc?.stockId === stckId)?.productName ??
      ""
    );
  };

  const getToLocationBasedOnVetDepartment = () => {
    return (
      locations?.find((li) => li?.department === getVetDepartment()) ?? null
    );
  };

  const onSaveAndSubmit = async () => {
    //for the purpose of storing stock issue list if the quantity is not available

    // if (getCompletedAdministerList()?.length > 0) {
    //   const statFacility =
    //     facilities?.find((li) => li?.facilityId === apntDet?.facilityId) ??
    //     null;
    //   // {
    //   //   name: apntDet?.facility,
    //   //   primaryStorage: 63,
    //   //   secondaryStorage: 59,
    //   //   facilityId: apntDet?.facilityId,
    //   // };
    //   const issueList = [];
    //   getCompletedAdministerList()?.forEach((li) => {
    //     const batchList = clinicStocks?.data?.find(
    //       (stc) => stc?.stockId === li?.stockId
    //     )?.stockBatch;
    //     const stockAvailInPrimStore = batchList?.find(
    //       (bat) => bat?.locationId === statFacility?.primaryStorage
    //     );
    //     if (stockAvailInPrimStore) return;
    //     const stockAvailInSecStore = batchList?.find(
    //       (bat) => bat?.locationId === statFacility?.secondaryStorage
    //     );
    //     const reqObj = {
    //       itemName: li?.procedureName ?? "",
    //       quantity: Number(stockAvailInSecStore ? li?.quantity : -li?.quantity),
    //       status: "Completed",
    //       stockId: li?.stockId ?? null,
    //       batchId: stockAvailInSecStore?.batchId ?? null,
    //       mode: "auto",
    //       isRejected: false,
    //       issueQuantity: Number(
    //         stockAvailInSecStore ? li?.quantity : -li?.quantity
    //       ),
    //     };
    //     if (stockAvailInSecStore) {
    //       reqObj.fromLocationId = stockAvailInSecStore?.locationId;
    //       reqObj.fromLocationName = stockAvailInSecStore?.location;
    //       reqObj.toLocationId = statFacility?.primaryStorage;
    //       reqObj.toLocation = statFacility?.name;
    //       issueList.push(reqObj);
    //       return;
    //     }
    //     reqObj.fromLocationId = statFacility?.primaryStorage;
    //     reqObj.fromLocationName = statFacility?.name;
    //     reqObj.toLocationId = statFacility?.primaryStorage;
    //     reqObj.toLocation = statFacility?.name;
    //     issueList.push(reqObj);
    //   });
    //   if (issueList?.length > 0) {
    //     const apiObj = {
    //       facilityId: statFacility?.facilityId,
    //       stockIssueDetails: issueList,
    //       requestedDate: moment(new Date()).format("YYYY-MM-DD"),
    //       requestedBy: apntDet?.doctorId ?? null,
    //     };
    //     dispatch(createStockIssue(apiObj));
    //   }
    // }

    // if (getCompletedAdministerList()?.length > 0) {
    //   const issueList = [];
    //   getCompletedAdministerList()?.forEach((li) => {
    //     const batchList = clinicStocks?.data?.find(
    //       (stc) => stc?.stockId === li?.stockId
    //     )?.stockBatch;
    //     const vetDeptMatchList = batchList?.filter(
    //       (bl) => bl?.department === getVetDepartment()
    //     );
    //     const batchMatchWithVetDept = vetDeptMatchList?.find(
    //       (bl) => bl?.batchId === li?.batchId
    //     );
    //     if (batchMatchWithVetDept && batchMatchWithVetDept?.quantity > 0) {
    //       return;
    //     }
    //     if (batchMatchWithVetDept && !batchMatchWithVetDept?.quantity > 0) {
    //       issueList.push({
    //         ...li,
    //         ...batchMatchWithVetDept,
    //         from: "Vet Department",
    //         issue: "Quantity not available",
    //         fromLocation: batchMatchWithVetDept?.location,
    //         fromLocationId: batchMatchWithVetDept?.locationId,
    //         toLocation: getToLocationBasedOnVetDepartment()?.locationName,
    //         toLocationId: getToLocationBasedOnVetDepartment()?.id,
    //         department: getVetDepartment(),
    //       });
    //       return;
    //     }
    //     const filteredList = batchList?.filter((batch) => {
    //       const location = locationList?.find(
    //         (loc) => loc?.id === batch?.locationId
    //       );
    //       if (!location) return false;
    //       // Condition 1: Clinic
    //       if (location?.mainLocation?.includes("Clinic")) {
    //         return true;
    //       }
    //       // Condition 2: Pharmacy or Pet Shop
    //       if (
    //         location?.mainLocation?.includes("Pharmacy") ||
    //         location?.mainLocation?.includes("Pet Shop")
    //       ) {
    //         return true;
    //       }
    //       return false;
    //     });
    //     const batchMatchWithLocation = filteredList?.find(
    //       (bl) => bl?.batchId === li?.batchId
    //     );
    //     if (batchMatchWithLocation) {
    //       issueList.push({
    //         ...li,
    //         ...batchMatchWithLocation,
    //         from: "Location",
    //         issue: "Quantity available",
    //         fromLocation: batchMatchWithLocation?.location,
    //         fromLocationId: batchMatchWithLocation?.locationId,
    //         toLocation: vetDeptMatchList?.[0]?.location,
    //         toLocationId: vetDeptMatchList?.[0]?.locationId,
    //         department: vetDeptMatchList?.[0]?.department,
    //       });
    //     }
    //   });
    //   if (issueList?.length > 0) {
    //     const stockIssueDetails = issueList?.map((li) => {
    //       const reqObj = {
    //         itemName: getProductNameByStockId(li?.stockId) ?? "",
    //         quantity: 1,
    //         status: "Completed",
    //         stockId: li?.stockId ?? null,
    //         batchId: li?.batchId ?? null,
    //         mode: "auto",
    //         department: li?.department ?? null,
    //         issueQuantity: 1,
    //         fromLocationName: li?.fromLocation ?? null,
    //         fromLocationId: li?.fromLocationId ?? null,
    //         toLocationName: li?.toLocation ?? null,
    //         toLocationId: li?.toLocationId ?? null,
    //       };
    //       return reqObj;
    //     });
    //     const apiObj = {
    //       stockIssueDetails,
    //       requestedDate: moment(new Date()).format("YYYY-MM-DD"),
    //       requestedBy: apntDet?.doctorId ?? null,
    //     };
    //     dispatch(createStockIssue(apiObj));
    //   }
    // }
    // handleFinalize();
    if (apntDet?.appointmentType === "Home") {
      dispatch(
        updateAppointmentStatusById({
          appointmentId: apntDet?.appointmentId,
          status: "finalize",
          successMsg: "Status updated successfully!",
        }),
      ).then((res) => {
        if (res?.payload) getcompliantSummary();
      });
    }
    if (apntDet?.appointmentType === "Physical") {
      dispatch(updateFinalize(apntDet?.appointmentId)).then((response) => {
        if (response?.payload) getcompliantSummary();
      });
    }
    setFinModVsble(false);
    // const finRes = await handleFinalize();
    // if (!finRes) return;
    const element = pdfRef?.current;
    if (!element) return;
    let url = "";
    const fileName = `${reqDet?.appointment?.petName}-${Date.now()}.pdf`;
    const opt = {
      margin: 0.5,
      filename: fileName,
      image: { type: "jpeg", quality: 0.75 },
      html2canvas: {
        scale: 1.5,
        useCORS: true,
        logging: false,
        letterRendering: true,
      },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    try {
      const pdfBlob = await html2pdf().from(element).set(opt).outputPdf("blob");
      // url = await uploadPdfToS3(pdfBlob, fileName);
      const form = new FormData();
      // form.append("pdf", url);
      form.append("pdf", pdfBlob, fileName);
      form.append("caseStatus", status);
      dispatch(
        updateCaseSummaryUrlByApntId({
          apntId: apntDet?.appointmentId,
          form,
        }),
      );
      // if (apntDet?.appointmentId) {
      //   dispatch(getPetsCompliantSummary(apntDet?.appointmentId));
      // } else if (apntDet?.petId) {
      //   dispatch(getPetDetailsById(apntDet?.petId));
      // }
      const dischargeRaw = reqDet?.dischargeSummary ?? [];
      const dischargeList = Array.isArray(dischargeRaw)
        ? dischargeRaw.filter(Boolean)
        : dischargeRaw && typeof dischargeRaw === "object"
          ? [dischargeRaw]
          : [];
      const latestDischarge = [...dischargeList].sort((a, b) => {
        const aId = Number(a?.dischargeSummaryId ?? a?.id ?? 0);
        const bId = Number(b?.dischargeSummaryId ?? b?.id ?? 0);
        if (aId !== bId) return bId - aId;
        return (
          new Date(b?.updatedDate ?? b?.createdDate ?? 0).getTime() -
          new Date(a?.updatedDate ?? a?.createdDate ?? 0).getTime()
        );
      })[0];
      const followUpDate = latestDischarge?.followUpDate;
      const petId =
        apntDet?.petId ??
        reqDet?.pet?.petId ??
        petAndApntData?.pet?.petId ??
        null;
      if (
        latestDischarge?.patientOutcome !== "Deceased" &&
        followUpDate &&
        petId
      ) {
        const timeMatch = String(latestDischarge?.followUpTime ?? "").match(
          /^(\d{2}:\d{2})/,
        );
        const time =
          timeMatch?.[1] ||
          (String(followUpDate).includes(":")
            ? moment(followUpDate).format("HH:mm")
            : "") ||
          "10:00";
        dispatch(
          petAppointment({
            petId,
            showFeedback: false,
            data: {
              doctorId: inpatData?.doctorId ?? apntDet?.doctorId,
              facility:
                apntDet?.facility ?? petAndApntData?.appointment?.facility,
              facilityId:
                apntDet?.facilityId ?? petAndApntData?.appointment?.facilityId,
              remarks: "follow up",
              bookingType: "Walk-in",
              consultationMode: "Physical",
              serviceType: "Consultation",
              immediate: false,
              appointmentDate: moment(followUpDate).format("YYYY-MM-DD"),
              visitType: "Follow-up",
              reason: [],
              appointmentStartTime: time,
              appointmentEndTime: adjustTime(time, 15),
              prevAppointmentId: apntDet?.appointmentId,
            },
          }),
        );
      }
      const petDetailsRoute = location?.pathname?.startsWith("/lab-pet-details")
        ? "/lab-pet-details"
        : "/clinic-pet-details";
      navigate(petDetailsRoute, {
        state: {
          bill: {
            paymentId: reqDet?.paymentId,
            appointmentId: apntDet?.appointmentId,
          },
          editBill: true,
          selectedTab: "billing",
          appointment: {
            appoinment: {
              appointmentId: apntDet?.appointmentId,
              petId: apntDet?.petId,
              userId: apntDet?.userId,
            },
          },
        },
      });
      // dispatch(
      //   getPetBillingById({
      //     petId: apntDet?.petId,
      //     data: { page: 1, itemSize: 5 },
      //   }),
      // ).then((res) => {
      //   if (res?.payload) {
      //     const reqData = res?.payload?.data?.find(
      //       (li) => li?.appointmentId === apntDet?.appointmentId,
      //     );
      //     if (reqData) {
      //       navigate("/clinic-pet-details", {
      //         state: {
      //           bill: reqData,
      //           editBill: true,
      //           selectedTab: "billing",
      //           appointment: {
      //             appoinment: {
      //               appointmentId: apntDet?.appointmentId,
      //               petId: apntDet?.petId,
      //             },
      //           },
      //         },
      //       });
      //     }
      //   }
      // });
    } catch (error) {
      console.log("PDF generation or upload failed:", error);
    }

    // html2pdf()
    //   .set({
    //     margin: 10,
    //     filename: "document.pdf",
    //     image: { type: "jpeg", quality: 0.98 },
    //   })
    //   .from(pdfRef.current)
    //   .save();
    // html2pdf()
    //   .from(pdfRef.current)
    //   .outputPdf("blob")
    //   .then((blob) => {});
  };

  const getReqPresList = () => {
    const unSavedWorkBook = localStorage.getItem("unSavedWorkbook");
    if (unSavedWorkBook) {
      const parsedValue = JSON.parse(unSavedWorkBook);
      if (parsedValue?.presList?.length > 0) {
        const reqData = [
          ...(reqDet?.prescription ?? []),
          {
            remark: "",
            id: Date.now(),
            i: reqDet?.prescription?.length ?? 0,
            prescriptionDetails: parsedValue?.presList,
          },
        ];
        return reqData;
      }
    }
    return reqDet?.prescription ?? [];
  };

  useEffect(() => {
    if (!finModVsble || typeof window === "undefined") return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = pdfRef?.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    const node = pdfRef?.current;
    const imgs = node ? [...node.querySelectorAll("img")] : [];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", warm, { once: true });
        img.addEventListener("error", warm, { once: true });
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", warm);
        img.removeEventListener("error", warm);
      });
    };
  }, [finModVsble, reqDet?.appointment?.petName]);

  const handlePrint = () => {
    printFromNode(pdfRef?.current, {
      title: `${reqDet?.appointment?.petName ?? ""}_${Date.now()}`,
      pageStyle: workbookHistoryPrintConfig,
    });
  };

  return (
    <>
      <div style={{ display: "none" }}>
        <ContentToPrint
          ref={pdfRef}
          rootClassName="workbook-history-print"
          footer={
            clinicDet?.footer?.length > 0 ? (
              <img src={clinicDet?.footer} alt="" />
            ) : null
          }
          header={
            <div className="flex-row-ali-cen mv20">
              <div className="w35Per">
                <div>
                  {clinicDet?.image ? (
                    <img src={clinicDet?.image} className="img-h75" alt="" />
                  ) : (
                    <div className="flex-center h50img">
                      <Typography className="font-bold fs30 white-color capitalize">
                        {clinicDet?.name?.[0]}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
              <div className="w30Per flex-center">
                <div className="font-bold fs20 blue-color">Medical Record</div>
              </div>
              <div className="w35Per flex1-end">
                <div className="prescription-doc-header-clinic">
                  {/* <div className="font-bold fs12 blue-color">
                    Business Name:
                  </div> */}
                  <div className="font-bold blue-color fs16">
                    {clinicDet?.name ?? ""}
                  </div>
                  {/* <div className="font-bold fs12 blue-color mt5">
                    Business Address:
                  </div> */}
                  <div className="txt-regular fs12 blue-color">
                    {clinicDet?.address ?? ""}
                  </div>
                  {clinicDet?.taxId?.length > 0 ? (
                    <div className="flex-row-ali-cen mt5">
                      <div className="font-bold fs12 blue-color">
                        {/* CLINIC  */}
                        TAX ID:{" "}
                      </div>
                      <div className="txt-regular fs12 blue-color ml3">
                        {clinicDet?.taxId ?? ""}
                      </div>
                    </div>
                  ) : null}
                  {clinicDet?.mobile?.length > 0 ? (
                    <div className="flex-row-ali-cen mt5">
                      <div className="font-bold fs12 blue-color">Contact: </div>
                      <div className="txt-regular fs12 blue-color ml3">
                        {clinicDet?.mobile ?? ""}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          }
        >
          <tr>
            <td className="prescription-print-tbody-cell">
              <div className="prescription-print-body-inner">
                <div className="p5">
                  <div className="mh20 mb20">
                    <div className="flex-row-ali-cen">
                      <div className="w100Per">
                        <div className="flex-row-ali-cen">
                          <Typography className="fs14 blue-color font-bold">
                            Case ID - {reqDet?.appointment?.caseId}
                          </Typography>
                          <Typography
                            className={`font-medium fs10 pv2 ph10 white-color br5 flex-center ml10 ${
                              reqDet?.appointment?.appointmentType ===
                              "Physical"
                                ? "card-bot-blue-back"
                                : "virtual-bg"
                            }`}
                          >
                            {reqDet?.appointment?.appointmentType === "Home"
                              ? "Home Visit"
                              : reqDet?.appointment?.appointmentType}
                          </Typography>
                          <Typography className="light-blue fs14 txt-semi-bold ml10">
                            {reqDet?.appointment?.serviceType}
                            {reqDet?.appointment?.serviceType ===
                              "Consultation" &&
                            reqDet?.appointment?.reason?.length > 0
                              ? ` | ${
                                  Array.isArray(reqDet?.appointment?.reason)
                                    ? reqDet?.appointment?.reason?.join(", ")
                                    : (reqDet?.appointment?.reason ?? "")
                                }`
                              : ""}
                          </Typography>
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          {reqDet?.appointment?.userName?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Customer Name:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {reqDet?.appointment?.userName}
                              </Typography>
                            </div>
                          ) : null}
                          {reqDet?.appointment?.petName?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Pet Name:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {reqDet?.appointment?.petName}
                              </Typography>
                            </div>
                          ) : null}
                          {reqDet?.appointment?.petType?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Species:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {reqDet?.appointment?.petType}
                              </Typography>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          {reqDet?.appointment?.breed?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Breed:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {reqDet?.appointment?.breed}
                              </Typography>
                            </div>
                          ) : null}
                          {genderEnum?.[reqDet?.appointment?.gender]?.length >
                          0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Gender:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {genderEnum?.[reqDet?.appointment?.gender]}
                              </Typography>
                            </div>
                          ) : null}
                          {reqDet?.appointment?.weight?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Weight:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {reqDet?.appointment?.weight}
                              </Typography>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          <div className="flex-row-ali-cen">
                            <img
                              src={require("../../../../assets/images/png/calen.png")}
                              className="img-wh15"
                            />
                            <Typography className="yellow2 fs14 txt-semi-bold ml5">
                              {moment(
                                new Date(reqDet?.appointment?.appointmentDate),
                              ).format("DD MMM YYYY")}
                            </Typography>
                          </div>
                          <div className="flex-row-ali-cen ml10">
                            <img
                              src={require("../../../../assets/images/png/clock.png")}
                              className="img-wh15"
                            />
                            <Typography className="yellow2 fs14 txt-semi-bold ml5">
                              {reqDet?.appointment?.appointmentStartTime ??
                                moment(
                                  new Date(
                                    reqDet?.appointment?.appointmentdateTime,
                                  ),
                                ).format("HH:mm")}
                            </Typography>
                          </div>
                          <div className="flex-row-ali-cen ml10">
                            <img
                              src={require("../../../../assets/images/png/steath.png")}
                              className="img-wh20"
                            />
                            <Typography className="yellow2 fs14 txt-semi-bold ml5">
                              {getRightSaluation(
                                clinicVets,
                                reqDet?.appointment?.doctorId,
                              )}
                              {reqDet?.appointment?.doctorName}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="gry-hor-split mv15" />
                    {isEmrEnabled("admissionRemarks") ? (
                      <AdmissionRemarkView remarks={inpatData?.remarks} />
                    ) : null}
                    {isEmrEnabled("redFlags") ? (
                      <InpatientRedFlagsView
                        inpatientAlerts={inpatData?.inpatientAlert ?? []}
                      />
                    ) : null}
                    {isEmrEnabled("inpatientInstructions") ? (
                      <InpatientInstructionsView
                        inpatientSummary={inpatData?.inpatientSummary}
                      />
                    ) : null}
                    {/* {reqDet?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                        reqDet?.appointment?.appointmentId,
                    )?.conclusion?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold">
                          Overview
                        </Typography>

                        <Typography className="fs12 txt-regular black">
                          {
                            reqDet?.clinicalNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                reqDet?.appointment?.appointmentId,
                            )?.conclusion
                          }
                        </Typography>
                      </>
                    ) : null} */}
                    {isEmrEnabled("soapNotes") &&
                    (reqDet?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId &&
                        cn?.consolidated,
                    ) ??
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                            reqDet?.appointment?.appointmentId &&
                          !cn?.consolidated,
                      )) ? (
                      <>
                        <div
                          className="flex-row-ali-cen mt10 cursor workbook-print-section"
                          onClick={() =>
                            handleOpen("soapNotes", !openValues?.soapNotes)
                          }
                        >
                          <Typography className="fs14 blue-color font-bold">
                            Soap Notes
                          </Typography>
                        </div>
                        {openValues?.soapNotes && (
                          <SoapNoteView
                            apntId={apntId}
                            soapNotes={reqDet?.clinicalNotes?.filter(
                              (cn) =>
                                cn?.appointmentId ===
                                reqDet?.appointment?.appointmentId,
                            )}
                            // soapNote={
                            //   reqDet?.clinicalNotes?.find(
                            //     (cn) =>
                            //       cn?.appointmentId ===
                            //         reqDet?.appointment?.appointmentId &&
                            //       cn?.consolidated,
                            //   ) ??
                            //   reqDet?.clinicalNotes?.find(
                            //     (cn) =>
                            //       cn?.appointmentId ===
                            //         reqDet?.appointment?.appointmentId &&
                            //       !cn?.consolidated,
                            //   )
                            // }
                          />
                        )}
                      </>
                    ) : null}
                    {isEmrEnabled("procedures") &&
                    reqDet?.procedures?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Procedures
                        </Typography>
                        <div className="mt10" />
                        <ProceduresView procedures={reqDet?.procedures} />
                      </>
                    ) : null}
                    {isEmrEnabled("administered") &&
                    reqDet?.procedures?.length > 0 &&
                    reqDet?.procedures
                      ?.flatMap((proc) => proc?.procedureDetails || [])
                      ?.filter(
                        (det) =>
                          det?.type === "Administered" &&
                          det?.status === "true",
                      )?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Administered
                        </Typography>
                        <div className="mt10" />
                        <ProceduresView
                          procedures={reqDet?.procedures}
                          isAdminister
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("notes") &&
                    reqDet?.appointment?.notes?.length > 0 ? (
                      <>
                        <div className="prescription-print-notes workbook-print-section">
                          <Typography className={`fs12 txt-semi-bold black`}>
                            Memo
                          </Typography>
                          <Typography
                            className="fs12 txt-regular black"
                            style={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                            component="div"
                          >
                            {(reqDet?.appointment?.notes ?? "")
                              ?.split("\n")
                              ?.map((line, index) => (
                                <div key={index}>{line || "\u00A0"}</div>
                              ))}
                          </Typography>
                        </div>
                      </>
                    ) : null}
                    {isEmrEnabled("preventive") &&
                    reqDet?.prevetive?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Preventive
                        </Typography>
                        <PreventiveView preventives={reqDet?.prevetive} />
                      </>
                    ) : null}
                    {isEmrEnabled("diagnostics") &&
                    reqDet?.diagnostics?.filter(
                      (li) => li?.status?.toLowerCase() !== "scheduled",
                    )?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Diagnostics
                        </Typography>
                        <div className="mt10" />
                        <DiagnosticsView
                          diagnostics={reqDet?.diagnostics?.filter(
                            (li) => li?.status?.toLowerCase() !== "scheduled",
                          )}
                          showFileAtBottom
                          isFilePreview
                          smallSize
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("prescription") &&
                    getReqPresList()?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Prescription
                        </Typography>
                        <div className="mt10" />
                        <PrescriptionView
                          prescriptions={getReqPresList()}
                          showShortName={true}
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("consentForms") &&
                    reqDet?.forms?.filter((li) => li?.status)?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Consent Form
                        </Typography>
                        <FormsView
                          forms={reqDet?.forms?.filter((li) => li?.status)}
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("protocolForms") &&
                    (reqDet?.treatmentPlas ?? [])?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Protocol Form
                        </Typography>
                        <TreatmentPlanView
                          plans={reqDet?.treatmentPlas ?? []}
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("ioMonitoring") &&
                    getIoMonitoringList(inpatData?.ioMonitoring).length > 0 ? (
                      <div className="workbook-print-section">
                        <IOMonitoringSummaryView
                          ioMonitoring={inpatData?.ioMonitoring}
                        />
                      </div>
                    ) : null}
                    {isEmrEnabled("vitals") && reqDet?.vitals?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Vitals
                        </Typography>
                        <VitalsView vitals={reqDet?.vitals} />
                      </>
                    ) : null}
                    {isEmrEnabled("bodyMap") && reqDet?.bodymaps?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Body Map
                        </Typography>
                        <BodyMapView bodyMaps={reqDet?.bodymaps} />
                      </>
                    ) : null}
                    {isEmrEnabled("files") &&
                    reqDet?.petFiles?.filter((fle) => fle?.appointmentId)
                      ?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Files
                        </Typography>
                        <FileView
                          files={reqDet?.petFiles?.filter(
                            (fle) => fle?.appointmentId,
                          )}
                        />
                      </>
                    ) : null}
                    {/* {reqDet?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                        reqDet?.appointment?.appointmentId,
                    )?.keyIssues?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10">
                          Key Issues
                        </Typography>

                        <Typography className="fs12 txt-regular black">
                          {
                            reqDet?.clinicalNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                reqDet?.appointment?.appointmentId,
                            )?.keyIssues
                          }
                        </Typography>
                      </>
                    ) : null} */}
                    {/* {reqDet?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                        reqDet?.appointment?.appointmentId,
                    )?.abnormalFindings?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Abnormal Findings
                        </Typography>

                        <Typography className="fs12 txt-regular black">
                          {
                            reqDet?.clinicalNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                reqDet?.appointment?.appointmentId,
                            )?.abnormalFindings
                          }
                        </Typography>
                      </>
                    ) : null}
                    {reqDet?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                        reqDet?.appointment?.appointmentId,
                    )?.customerInstructions?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Customer Instructions
                        </Typography>

                        <Typography className="fs12 txt-regular black">
                          {
                            reqDet?.clinicalNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                reqDet?.appointment?.appointmentId,
                            )?.customerInstructions
                          }
                        </Typography>
                      </>
                    ) : null}
                    {reqDet?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                        reqDet?.appointment?.appointmentId,
                    )?.reminders?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Reminders
                        </Typography>

                        <Typography className="fs12 txt-regular black">
                          {
                            reqDet?.clinicalNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                reqDet?.appointment?.appointmentId,
                            )?.reminders
                          }
                        </Typography>
                      </>
                    ) : null} */}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ContentToPrint>
        {/* <div
        //  ref={pdfRef}
        >
          <div className="his-bodr">
            <div className="flex-row-ali-cen mv20">
              <div className="w20Per">
                <div className="pl20">
                  <div>
                    {profileImg ? (
                      <img src={profileImg} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {profile?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w60Per flex-center">
                <div className="font-bold fs20 blue-color">Medical Record</div>
              </div>
              <div className="w20Per flex1-end">
              </div>
            </div>
            <div className="mh20 mb20">
              <div className="flex-row-ali-cen">
                <div className="w70Per">
                  <div className="flex-row-ali-cen">
                    <Typography className="fs14 blue-color font-bold">
                      Case ID - {reqDet?.appointment?.caseId}
                    </Typography>
                    <Typography className="light-blue fs14 txt-semi-bold ml10">
                      {reqDet?.appointment?.serviceType}
                      {reqDet?.appointment?.serviceType === "Consultation" &&
                      reqDet?.appointment?.reason?.length > 0
                        ? ` | ${
                            Array.isArray(reqDet?.appointment?.reason)
                              ? reqDet?.appointment?.reason?.join(", ")
                              : (reqDet?.appointment?.reason ?? "")
                          }`
                        : ""}
                    </Typography>
                  </div>
                  <div className="flex-row-ali-cen mt10">
                    {reqDet?.appointment?.userName?.length > 0 ? (
                      <div className="flex-row-ali-cen">
                        <Typography className="blue-color fs14 txt-semi-bold">
                          Customer Name:
                        </Typography>
                        <Typography className="black fs14 font-medium ml5">
                          {reqDet?.appointment?.userName}
                        </Typography>
                      </div>
                    ) : null}
                    {reqDet?.appointment?.petName?.length > 0 ? (
                      <div className="flex-row-ali-cen ml10">
                        <Typography className="blue-color fs14 txt-semi-bold">
                          Pet Name:
                        </Typography>
                        <Typography className="black fs14 font-medium ml5">
                          {reqDet?.appointment?.petName}
                        </Typography>
                      </div>
                    ) : null}
                    {reqDet?.appointment?.petType?.length > 0 ? (
                      <div className="flex-row-ali-cen ml10">
                        <Typography className="blue-color fs14 txt-semi-bold">
                          Species:
                        </Typography>
                        <Typography className="black fs14 font-medium ml5">
                          {reqDet?.appointment?.petType}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex-row-ali-cen mt10">
                    {reqDet?.appointment?.breed?.length > 0 ? (
                      <div className="flex-row-ali-cen">
                        <Typography className="blue-color fs14 txt-semi-bold">
                          Breed:
                        </Typography>
                        <Typography className="black fs14 font-medium ml5">
                          {reqDet?.appointment?.breed}
                        </Typography>
                      </div>
                    ) : null}
                    {genderEnum?.[reqDet?.appointment?.gender]?.length > 0 ? (
                      <div className="flex-row-ali-cen ml10">
                        <Typography className="blue-color fs14 txt-semi-bold">
                          Gender:
                        </Typography>
                        <Typography className="black fs14 font-medium ml5">
                          {genderEnum?.[reqDet?.appointment?.gender]}
                        </Typography>
                      </div>
                    ) : null}
                    {reqDet?.appointment?.weight?.length > 0 ? (
                      <div className="flex-row-ali-cen ml10">
                        <Typography className="blue-color fs14 txt-semi-bold">
                          Weight:
                        </Typography>
                        <Typography className="black fs14 font-medium ml5">
                          {reqDet?.appointment?.weight}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex-row-ali-cen mt10">
                    <div className="flex-row-ali-cen">
                      <img
                        src={require("../../../../assets/images/png/calen.png")}
                        className="img-wh15"
                      />
                      <Typography className="yellow2 fs14 txt-semi-bold ml5">
                        {moment(
                          new Date(reqDet?.appointment?.appointmentDate),
                        ).format("DD MMM YYYY")}
                      </Typography>
                    </div>
                    <div className="flex-row-ali-cen ml10">
                      <img
                        src={require("../../../../assets/images/png/clock.png")}
                        className="img-wh15"
                      />
                      <Typography className="yellow2 fs14 txt-semi-bold ml5">
                        {reqDet?.appointment?.appointmentStartTime ??
                          moment(
                            new Date(reqDet?.appointment?.appointmentdateTime),
                          ).format("HH:mm")}
                      </Typography>
                    </div>
                    <div className="flex-row-ali-cen ml10">
                      <img
                        src={require("../../../../assets/images/png/steath.png")}
                        className="img-wh20"
                      />
                      <Typography className="yellow2 fs14 txt-semi-bold ml5">
                        {getRightSaluation(
                          clinicVets,
                          reqDet?.appointment?.doctorId,
                        )}
                        {reqDet?.appointment?.doctorName}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
              <div className="gry-hor-split mv15" />
              {reqDet?.clinicalNotes?.find(
                (cn) =>
                  cn?.appointmentId === reqDet?.appointment?.appointmentId,
              )?.conclusion?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold">
                    Overview
                  </Typography>

                  <Typography className="fs12 txt-regular black">
                    {
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId,
                      )?.conclusion
                    }
                  </Typography>
                </>
              ) : null}
              {reqDet?.procedures?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Procedures
                  </Typography>
                  <div className="mt10" />
                  <ProceduresView procedures={reqDet?.procedures} />
                </>
              ) : null}
              {reqDet?.procedures?.length > 0 &&
              reqDet?.procedures
                ?.flatMap((proc) => proc?.procedureDetails || [])
                ?.filter(
                  (det) =>
                    det?.type === "Administered" && det?.status === "true",
                )?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Administered
                  </Typography>
                  <div className="mt10" />
                  <ProceduresView
                    procedures={reqDet?.procedures}
                    isAdminister
                  />
                </>
              ) : null}
              {reqDet?.prevetive?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Preventive
                  </Typography>
                  <PreventiveView preventives={reqDet?.prevetive} />
                </>
              ) : null}
              {reqDet?.diagnostics?.filter(
                (li) => li?.dueDate === reqDet?.appointment?.appointmentDate,
              )?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Diagnostics
                  </Typography>
                  <div className="mt10" />
                  <DiagnosticsView
                    diagnostics={reqDet?.diagnostics?.filter(
                      (li) =>
                        li?.dueDate === reqDet?.appointment?.appointmentDate,
                    )}
                  />
                </>
              ) : null}
              {getReqPresList()?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Prescription
                  </Typography>
                  <div className="mt10" />
                  <PrescriptionView prescriptions={getReqPresList()} />
                </>
              ) : null}
              {reqDet?.forms?.filter((li) => li?.status)?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Consent Form
                  </Typography>
                  <FormsView
                    forms={reqDet?.forms?.filter((li) => li?.status)}
                  />
                </>
              ) : null}
              {reqDet?.vitals?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Vitals
                  </Typography>
                  <VitalsView vitals={reqDet?.vitals} />
                </>
              ) : null}
              {reqDet?.bodymaps?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Body Map
                  </Typography>
                  <BodyMapView bodyMaps={reqDet?.bodymaps} />
                </>
              ) : null}
              {reqDet?.files?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Files
                  </Typography>
                  <FileView files={reqDet?.files} />
                </>
              ) : null}
              {reqDet?.appointment?.notes?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Notes
                  </Typography>
                  <Typography
                    className="fs12 txt-regular black"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                    component="div"
                  >
                    {(reqDet?.appointment?.notes ?? "")
                      ?.split("\n")
                      ?.map((line, index) => (
                        <div key={index}>{line || "\u00A0"}</div>
                      ))}
                  </Typography>
                </>
              ) : null}
              {reqDet?.clinicalNotes?.find(
                (cn) =>
                  cn?.appointmentId === reqDet?.appointment?.appointmentId,
              )?.keyIssues?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Key Issues
                  </Typography>

                  <Typography className="fs12 txt-regular black">
                    {
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId,
                      )?.keyIssues
                    }
                  </Typography>
                </>
              ) : null}
              {reqDet?.clinicalNotes?.find(
                (cn) =>
                  cn?.appointmentId === reqDet?.appointment?.appointmentId,
              )?.abnormalFindings?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Abnormal Findings
                  </Typography>

                  <Typography className="fs12 txt-regular black">
                    {
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId,
                      )?.abnormalFindings
                    }
                  </Typography>
                </>
              ) : null}
              {reqDet?.clinicalNotes?.find(
                (cn) =>
                  cn?.appointmentId === reqDet?.appointment?.appointmentId,
              )?.customerInstructions?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Customer Instructions
                  </Typography>

                  <Typography className="fs12 txt-regular black">
                    {
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId,
                      )?.customerInstructions
                    }
                  </Typography>
                </>
              ) : null}
              {reqDet?.clinicalNotes?.find(
                (cn) =>
                  cn?.appointmentId === reqDet?.appointment?.appointmentId,
              )?.reminders?.length > 0 ? (
                <>
                  <Typography className="fs14 blue-color font-bold mt10">
                    Reminders
                  </Typography>

                  <Typography className="fs12 txt-regular black">
                    {
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId,
                      )?.reminders
                    }
                  </Typography>
                </>
              ) : null}
            </div>
          </div>
        </div> */}
      </div>
      <CustomModal
        open={finModVsble}
        onClose={onCloseMod}
        header=""
        headerCenter
        headerBold
        modal
        modalWidth={80}
      >
        <div className="scroll-80vh">
          <ContentToPrint
            ref={null}
            footer={
              clinicDet?.footer?.length > 0 ? (
                <img src={clinicDet?.footer} alt="" />
              ) : null
            }
            header={
              <div className="flex-row-ali-cen mv20">
                <div className="w35Per">
                  <div>
                    {clinicDet?.image ? (
                      <img src={clinicDet?.image} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {clinicDet?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w30Per flex-center">
                  <div className="font-bold fs20 blue-color">
                    Medical Record
                  </div>
                </div>
                <div className="w35Per flex1-end">
                  <div className="prescription-doc-header-clinic">
                    <div className="font-bold blue-color fs16">
                      {clinicDet?.name ?? ""}
                    </div>
                    <div className="txt-regular fs12 blue-color">
                      {clinicDet?.address ?? ""}
                    </div>
                    {clinicDet?.taxId?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          TAX ID:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.taxId ?? ""}
                        </div>
                      </div>
                    ) : null}
                    {clinicDet?.mobile?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          Contact:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.mobile ?? ""}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            }
          >
            <tr>
              <td className="prescription-print-tbody-cell">
                <div className="prescription-print-body-inner">
                  <div className="p5">
                    <div className="mh20 mb20">
                      <div className="flex-row-ali-cen">
                        <div className="w100Per">
                          <div className="flex-row-ali-cen">
                            <Typography className="fs14 blue-color font-bold">
                              Case ID - {reqDet?.appointment?.caseId}
                            </Typography>
                            <Typography
                              className={`font-medium fs10 pv2 ph10 white-color br5 flex-center ml10 ${
                                reqDet?.appointment?.appointmentType ===
                                "Physical"
                                  ? "card-bot-blue-back"
                                  : "virtual-bg"
                              }`}
                            >
                              {reqDet?.appointment?.appointmentType === "Home"
                                ? "Home Visit"
                                : reqDet?.appointment?.appointmentType}
                            </Typography>
                            <Typography className="light-blue fs14 txt-semi-bold ml10">
                              {reqDet?.appointment?.serviceType}
                              {reqDet?.appointment?.serviceType ===
                                "Consultation" &&
                              reqDet?.appointment?.reason?.length > 0
                                ? ` | ${
                                    Array.isArray(reqDet?.appointment?.reason)
                                      ? reqDet?.appointment?.reason?.join(", ")
                                      : (reqDet?.appointment?.reason ?? "")
                                  }`
                                : ""}
                            </Typography>
                          </div>
                          <div className="flex-row-ali-cen mt10">
                            {reqDet?.appointment?.userName?.length > 0 ? (
                              <div className="flex-row-ali-cen">
                                <Typography className="blue-color fs14 txt-semi-bold">
                                  Customer Name:
                                </Typography>
                                <Typography className="black fs14 font-medium ml5">
                                  {reqDet?.appointment?.userName}
                                </Typography>
                              </div>
                            ) : null}
                            {reqDet?.appointment?.petName?.length > 0 ? (
                              <div className="flex-row-ali-cen ml10">
                                <Typography className="blue-color fs14 txt-semi-bold">
                                  Pet Name:
                                </Typography>
                                <Typography className="black fs14 font-medium ml5">
                                  {reqDet?.appointment?.petName}
                                </Typography>
                              </div>
                            ) : null}
                            {reqDet?.appointment?.petType?.length > 0 ? (
                              <div className="flex-row-ali-cen ml10">
                                <Typography className="blue-color fs14 txt-semi-bold">
                                  Species:
                                </Typography>
                                <Typography className="black fs14 font-medium ml5">
                                  {reqDet?.appointment?.petType}
                                </Typography>
                              </div>
                            ) : null}
                          </div>
                          <div className="flex-row-ali-cen mt10">
                            {reqDet?.appointment?.breed?.length > 0 ? (
                              <div className="flex-row-ali-cen">
                                <Typography className="blue-color fs14 txt-semi-bold">
                                  Breed:
                                </Typography>
                                <Typography className="black fs14 font-medium ml5">
                                  {reqDet?.appointment?.breed}
                                </Typography>
                              </div>
                            ) : null}
                            {genderEnum?.[reqDet?.appointment?.gender]?.length >
                            0 ? (
                              <div className="flex-row-ali-cen ml10">
                                <Typography className="blue-color fs14 txt-semi-bold">
                                  Gender:
                                </Typography>
                                <Typography className="black fs14 font-medium ml5">
                                  {genderEnum?.[reqDet?.appointment?.gender]}
                                </Typography>
                              </div>
                            ) : null}
                            {reqDet?.appointment?.weight?.length > 0 ? (
                              <div className="flex-row-ali-cen ml10">
                                <Typography className="blue-color fs14 txt-semi-bold">
                                  Weight:
                                </Typography>
                                <Typography className="black fs14 font-medium ml5">
                                  {reqDet?.appointment?.weight}
                                </Typography>
                              </div>
                            ) : null}
                          </div>
                          <div className="flex-row-ali-cen mt10">
                            <div className="flex-row-ali-cen">
                              <img
                                src={require("../../../../assets/images/png/calen.png")}
                                className="img-wh15"
                              />
                              <Typography className="yellow2 fs14 txt-semi-bold ml5">
                                {moment(
                                  new Date(
                                    reqDet?.appointment?.appointmentDate,
                                  ),
                                ).format("DD MMM YYYY")}
                              </Typography>
                            </div>
                            <div className="flex-row-ali-cen ml10">
                              <img
                                src={require("../../../../assets/images/png/clock.png")}
                                className="img-wh15"
                              />
                              <Typography className="yellow2 fs14 txt-semi-bold ml5">
                                {reqDet?.appointment?.appointmentStartTime ??
                                  moment(
                                    new Date(
                                      reqDet?.appointment?.appointmentdateTime,
                                    ),
                                  ).format("HH:mm")}
                              </Typography>
                            </div>
                            <div className="flex-row-ali-cen ml10">
                              <img
                                src={require("../../../../assets/images/png/steath.png")}
                                className="img-wh20"
                              />
                              <Typography className="yellow2 fs14 txt-semi-bold ml5">
                                {getRightSaluation(
                                  clinicVets,
                                  reqDet?.appointment?.doctorId,
                                )}
                                {reqDet?.appointment?.doctorName}
                              </Typography>
                            </div>
                          </div>
                        </div>
                        {!isSumamryView ? (
                          <div className="w30Per">
                            <CustomSelect
                              select
                              list={workbookStatusList}
                              value={status}
                              handleChange={(e) => setStatus(e?.target?.value)}
                              label="Condition Status"
                            />
                          </div>
                        ) : null}
                      </div>
                      <div className="gry-hor-split mv15" />
                      <AdmissionRemarkView remarks={inpatData?.remarks} />
                      <InpatientRedFlagsView
                        inpatientAlerts={inpatData?.inpatientAlert ?? []}
                      />
                      <InpatientInstructionsView
                        inpatientSummary={inpatData?.inpatientSummary}
                      />
                      {/* {reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          reqDet?.appointment?.appointmentId,
                      )?.conclusion?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("overview", !openValues?.overview)
                            }
                          >
                            {openValues?.overview ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Overview
                            </Typography>
                          </div>
                          {openValues?.overview ? (
                            <Typography className="fs12 txt-regular black">
                              {
                                reqDet?.clinicalNotes?.find(
                                  (cn) =>
                                    cn?.appointmentId ===
                                    reqDet?.appointment?.appointmentId,
                                )?.conclusion
                              }
                            </Typography>
                          ) : null}
                        </>
                      ) : null} */}
                      {(reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                            reqDet?.appointment?.appointmentId &&
                          cn?.consolidated,
                      ) ??
                      reqDet?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                            reqDet?.appointment?.appointmentId &&
                          !cn?.consolidated,
                      )) ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("soapNotes", !openValues?.soapNotes)
                            }
                          >
                            {openValues?.soapNotes ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Soap Notes
                            </Typography>
                          </div>
                          {openValues?.soapNotes && (
                            <SoapNoteView
                              apntId={apntId}
                              soapNotes={reqDet?.clinicalNotes?.filter(
                                (cn) =>
                                  cn?.appointmentId ===
                                  reqDet?.appointment?.appointmentId,
                              )}
                            />
                          )}
                        </>
                      ) : null}
                      {reqDet?.procedures?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("procedures", !openValues?.procedures)
                            }
                          >
                            {openValues?.procedures ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Procedures
                            </Typography>
                          </div>
                          {openValues?.procedures && (
                            <ProceduresView procedures={reqDet?.procedures} />
                          )}
                        </>
                      ) : null}
                      {reqDet?.procedures?.length > 0 &&
                      reqDet?.procedures
                        ?.flatMap((proc) => proc?.procedureDetails || [])
                        ?.filter(
                          (det) =>
                            det?.type === "Administered" &&
                            det?.status === "true",
                        )?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen(
                                "administered",
                                !openValues?.administered,
                              )
                            }
                          >
                            {openValues?.administered ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Administered
                            </Typography>
                          </div>
                          {openValues?.administered && (
                            <ProceduresView
                              procedures={reqDet?.procedures}
                              isAdminister
                            />
                          )}
                        </>
                      ) : null}
                      {reqDet?.appointment?.notes?.length > 0 ? (
                        <>
                          <div className="prescription-print-notes">
                            <Typography className={`fs12 txt-semi-bold black`}>
                              Memo
                            </Typography>
                            <Typography
                              className="fs12 txt-regular black"
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                              component="div"
                            >
                              {(reqDet?.appointment?.notes ?? "")
                                ?.split("\n")
                                ?.map((line, index) => (
                                  <div key={index}>{line || "\u00A0"}</div>
                                ))}
                            </Typography>
                          </div>
                        </>
                      ) : null}
                      {reqDet?.prevetive?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("preventive", !openValues?.preventive)
                            }
                          >
                            {openValues?.preventive ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Preventive
                            </Typography>
                          </div>
                          {openValues?.preventive && (
                            <PreventiveView preventives={reqDet?.prevetive} />
                          )}
                        </>
                      ) : null}
                      {reqDet?.diagnostics?.filter(
                        (li) => li?.status?.toLowerCase() !== "scheduled",
                      )?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen(
                                "diagnostics",
                                !openValues?.diagnostics,
                              )
                            }
                          >
                            {openValues?.diagnostics ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Diagnostics
                            </Typography>
                          </div>
                          {openValues?.diagnostics && (
                            <DiagnosticsView
                              diagnostics={reqDet?.diagnostics?.filter(
                                (li) =>
                                  li?.status?.toLowerCase() !== "scheduled",
                              )}
                              showFileAtBottom
                              isFilePreview
                              smallSize
                            />
                          )}
                        </>
                      ) : null}
                      {getReqPresList()?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen(
                                "prescription",
                                !openValues?.prescription,
                              )
                            }
                          >
                            {openValues?.prescription ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Prescription
                            </Typography>
                          </div>
                          {openValues?.prescription && (
                            <PrescriptionView
                              prescriptions={getReqPresList()}
                              showShortName={true}
                            />
                          )}
                        </>
                      ) : null}
                      {(reqDet?.inpatientNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                            reqDet?.appointment?.appointmentId &&
                          cn?.type === "surgery" &&
                          cn?.consolidated,
                      ) ??
                      reqDet?.inpatientNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                            reqDet?.appointment?.appointmentId &&
                          cn?.type === "surgery",
                      )) ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("surgeryDoc", !openValues?.surgeryDoc)
                            }
                          >
                            {openValues?.surgeryDoc ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Surgery Documentation
                            </Typography>
                          </div>
                          {openValues?.surgeryDoc && (
                            <SoapNoteView
                              apntId={apntId}
                              soapNote={
                                reqDet?.inpatientNotes?.find(
                                  (cn) =>
                                    cn?.appointmentId ===
                                      reqDet?.appointment?.appointmentId &&
                                    cn?.type === "surgery" &&
                                    cn?.consolidated,
                                ) ??
                                reqDet?.inpatientNotes?.find(
                                  (cn) =>
                                    cn?.appointmentId ===
                                      reqDet?.appointment?.appointmentId &&
                                    cn?.type === "surgery",
                                )
                              }
                              isSurgery
                            />
                          )}
                        </>
                      ) : null}
                      {reqDet?.treatmentPlas?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen(
                                "treatmentPlan",
                                !openValues?.treatmentPlan,
                              )
                            }
                          >
                            {openValues?.treatmentPlan ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Protocol Form
                            </Typography>
                          </div>
                          {openValues?.treatmentPlan && (
                            <TreatmentPlanView plans={reqDet?.treatmentPlas} />
                          )}
                        </>
                      ) : null}
                      {reqDet?.forms?.filter((li) => li?.status)?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("forms", !openValues?.forms)
                            }
                          >
                            {openValues?.forms ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Consent Form
                            </Typography>
                          </div>
                          {openValues?.forms && (
                            <FormsView
                              forms={reqDet?.forms?.filter((li) => li?.status)}
                            />
                          )}
                        </>
                      ) : null}
                      {getIoMonitoringList(inpatData?.ioMonitoring).length >
                      0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen(
                                "ioMonitoring",
                                !openValues?.ioMonitoring,
                              )
                            }
                          >
                            {openValues?.ioMonitoring ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Monitoring & I/O Summary
                            </Typography>
                          </div>
                          {openValues?.ioMonitoring && (
                            <IOMonitoringSummaryView
                              ioMonitoring={inpatData?.ioMonitoring}
                              hideTitle
                            />
                          )}
                        </>
                      ) : null}
                      {reqDet?.vitals?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("vitals", !openValues?.vitals)
                            }
                          >
                            {openValues?.vitals ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Vitals
                            </Typography>
                          </div>
                          {openValues?.vitals && (
                            <VitalsView vitals={reqDet?.vitals} />
                          )}
                        </>
                      ) : null}
                      {reqDet?.bodymaps?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("bodyMap", !openValues?.bodyMap)
                            }
                          >
                            {openValues?.bodyMap ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Body Map
                            </Typography>
                          </div>
                          {openValues?.bodyMap && (
                            <BodyMapView bodyMaps={reqDet?.bodymaps} />
                          )}
                        </>
                      ) : null}
                      {reqDet?.petFiles?.filter((fle) => fle?.appointmentId)
                        ?.length > 0 ? (
                        <>
                          <div
                            className="flex-row-ali-cen mt10 cursor"
                            onClick={() =>
                              handleOpen("files", !openValues?.files)
                            }
                          >
                            {openValues?.files ? (
                              <ArrowDropDownIcon sx={iconSty} />
                            ) : (
                              <ArrowRightIcon sx={iconSty} />
                            )}
                            <Typography className="fs14 blue-color font-bold">
                              Files
                            </Typography>
                          </div>
                          {openValues?.files && (
                            <FileView
                              files={reqDet?.petFiles?.filter(
                                (fle) => fle?.appointmentId,
                              )}
                            />
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ContentToPrint>

          <div className="flex-center mt30">
            <div>
              {isSumamryView ? (
                <CustomButton text="Print" smallBtn onClick={handlePrint} />
              ) : (
                <CustomButton
                  text="Save & Submit"
                  smallBtn
                  onClick={onSaveAndSubmit}
                  disabled={isAnyApiInProgress}
                  grayBtn={isAnyApiInProgress}
                />
              )}
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default WorkBookHistory;
